// AgendaPage.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import EventListGSAP from '../components/EventListGSAP';
import { fetchEvents } from '../api/api';
import Loader from '../components/Loader';
import styles from '../styles/pages/_AgendaPage.module.scss';
import SEO from '../components/SEO';
import Billetterie from '../components/Billetterie';

// Utilitaire pour créer des slugs cohérents
const createSlug = (title) => {
  const titleText = typeof title === 'object' && title?.rendered 
    ? title.rendered 
    : title;

  if (!titleText || typeof titleText !== 'string') {
    console.warn('Titre invalide pour la création du slug:', title);
    return '';
  }
  
  return titleText
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Utilitaire pour formater les dates
const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('Date invalide');
    }
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Erreur lors du formatage de la date:', dateString, error);
    return null;
  }
};

// Fonction pour trouver un événement à partir d'un slug
const findEventFromSlug = (eventSlug, events) => {
  if (!eventSlug || !events?.length) return null;

  try {
    const slugMatch = eventSlug.match(/(.*)-(\d{4}-\d{2}-\d{2})$/);
    if (!slugMatch) {
      console.warn("Format de slug invalide:", eventSlug);
      return null;
    }

    const [, slug, date] = slugMatch;
    
    return events.find(event => {
      if (!event?.date || !event?.title) return false;
      
      const formattedDate = formatDate(event.date);
      if (!formattedDate) return false;

      const eventTitle = createSlug(event.title);
      if (!eventTitle) return false;

      const fullSlug = `${eventTitle}-${formattedDate}`;
      return fullSlug === `${slug}-${date}`;
    });
  } catch (error) {
    console.error('Erreur lors de la recherche de l\'événement:', error);
    return null;
  }
};

const AgendaPage = () => {
  const { eventSlug } = useParams();

  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['events'],
    queryFn: fetchEvents,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    onError: (error) => {
      console.error('Erreur lors de la récupération des événements:', error);
    },
  });

  const selectedEvent = React.useMemo(() => {
    if (!eventSlug || !events?.length) return null;

    const [eventName, date] = eventSlug.match(/(.*)-(\d{4}-\d{2}-\d{2})$/)?.slice(1) || [];
    if (!eventName || !date) return null;

    return events.find(event => {
      const eventTitle = createSlug(event.title?.rendered || event.title);
      const eventDate = new Date(event.date).toISOString().split('T')[0];
      return eventTitle === eventName && eventDate === date;
    });
  }, [eventSlug, events]);

  const structuredData = React.useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Poney Club Toulouse 2025",
      url: process.env.REACT_APP_BASE_URL,
      logo: `${process.env.REACT_APP_BASE_URL}/logo.png`,
      sameAs: [
        "https://www.facebook.com/PoneyClubToulouse",
        "https://twitter.com/PoneyFestival",
        "https://www.instagram.com/poneyclubtoulouse",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { 
          "@type": "ListItem", 
          position: 1, 
          name: "Accueil", 
          item: process.env.REACT_APP_BASE_URL 
        },
        { 
          "@type": "ListItem", 
          position: 2, 
          name: "programmation", 
          item: `${process.env.REACT_APP_BASE_URL}/programmation` 
        },
        ...(selectedEvent ? [{
          "@type": "ListItem",
          position: 3,
          name: selectedEvent.title,
          item: `${process.env.REACT_APP_BASE_URL}/programmation/${eventSlug}`,
        }] : []),
      ],
    },
    ...(selectedEvent ? [{
      "@context": "https://schema.org",
      "@type": "Event",
      name: selectedEvent.title,
      description: selectedEvent.description || "Description non disponible.",
      startDate: new Date(selectedEvent.date).toISOString(),
      endDate: new Date(new Date(selectedEvent.date).getTime() + 2 * 60 * 60 * 1000).toISOString(),
      image: selectedEvent.mainImage || `${process.env.REACT_APP_BASE_URL}/default-event-image.png`,
      performer: selectedEvent.artistePrincipal?.name
        ? { "@type": "PerformingGroup", name: selectedEvent.artistePrincipal.name }
        : undefined,
      location: {
        "@type": "Place",
        name: "Poney Club Toulouse",
        address: {
          "@type": "PostalAddress",
          streetAddress: "123 Avenue des Poneys",
          addressLocality: "Toulouse",
          postalCode: "31000",
          addressCountry: "FR",
        },
      },
      offers: {
        "@type": "Offer",
        url: `${process.env.REACT_APP_BASE_URL}/programmation/${eventSlug}`,
        price: selectedEvent.price || "0",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        validFrom: new Date().toISOString(),
      },
      organizer: {
        "@type": "Organization",
        name: "Poney Club Toulouse 2025",
        url: process.env.REACT_APP_BASE_URL,
      },
    }] : []),
  ], [selectedEvent, eventSlug]);

  const pageTitle = selectedEvent
    ? `${selectedEvent.title} - ${new Date(selectedEvent.date).toLocaleDateString('fr-FR')}`
    : "programmation";

  const pageDescription = selectedEvent
    ? `${selectedEvent.title} au Poney Club Toulouse le ${new Date(selectedEvent.date).toLocaleDateString('fr-FR')}. ${
        selectedEvent.description ? `${selectedEvent.description.slice(0, 150)}...` : ""
      }`
    : "Découvrez tous les événements à venir du Poney Club Toulouse - Spectacles équestres, animations et activités pour toute la famille.";

  const keywords = React.useMemo(() => Array.from(
    new Set([
      "événements équestres",
      "spectacles poneys",
      "programmation équestre",
      "Toulouse",
      "activités équestres",
      ...(selectedEvent ? [
        selectedEvent.title,
        selectedEvent.artistePrincipal?.name,
        "réservation spectacle équestre",
        "animation poney",
      ].filter(Boolean) : []),
    ])
  ), [selectedEvent]);

  // Séparation des événements à venir et passés
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventsAVenir = events.filter(e => {
    const dateFin = new Date(e.date_fin || e.date);
    dateFin.setHours(0, 0, 0, 0);
    return dateFin >= today;
  });
  const eventsPast = events.filter(e => {
    const dateFin = new Date(e.date_fin || e.date);
    dateFin.setHours(0, 0, 0, 0);
    return dateFin < today;
  });

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return (
      <div className={styles.errorMessage}>
        Une erreur s'est produite lors de la récupération des événements. Veuillez réessayer plus tard.
      </div>
    );
  }

  if (!selectedEvent && eventSlug) {
    return (
      <div className={styles.errorMessage}>
        L'événement demandé est introuvable. Vérifiez l'URL ou explorez les autres événements de notre programmation.
      </div>
    );
  }

  return (
    <div className={styles.agendaPage}>
      <SEO
        title={pageTitle}
        description={pageDescription}
        url={`/programmation${eventSlug ? `/${eventSlug}` : ''}`}
        type={selectedEvent ? "event" : "website"}
        keywords={keywords}
        image={selectedEvent?.mainImage || "/poneyseo.png"}
        structuredData={structuredData}
      />
       <h1 className={styles.title}>
        {selectedEvent ? selectedEvent.title : "programmation"}
      </h1>
      <EventListGSAP 
        events={eventsAVenir}
        selectedEvent={selectedEvent}
        eventSlug={eventSlug}
        isLoading={isLoading}
      />
      {eventsPast.length > 0 && (
        <>
          <h2 className={styles.pastEventsTitle}>Déjà passé</h2>
          <EventListGSAP 
            events={eventsPast}
            isPast={true}
          />
        </>
      )}
      <Billetterie/>
    </div>
  );
};

export default AgendaPage;